/* eslint-disable */
/* Code generated by ng-openapi-gen DO NOT EDIT. */
import { firstValueFrom } from "rxjs";

import { HttpClient, HttpContext } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { ApiConfiguration } from "../api-configuration";
import { BaseService } from "../base-service";
import { getServiceDdosTraffic } from "../fn/traffic/get-service-ddos-traffic";
import { GetServiceDdosTraffic$Params } from "../fn/traffic/get-service-ddos-traffic";
import { getServiceTrafficBps } from "../fn/traffic/get-service-traffic-bps";
import { GetServiceTrafficBps$Params } from "../fn/traffic/get-service-traffic-bps";
import { getServiceTrafficPackets } from "../fn/traffic/get-service-traffic-packets";
import { GetServiceTrafficPackets$Params } from "../fn/traffic/get-service-traffic-packets";
import { DdosRate } from "../models/ddos-rate";
import { Traffic } from "../models/traffic";
import { StrictHttpResponse } from "../strict-http-response";

/**
 * These endpoints replace the `/stats/{subscription_id}(/{subscription_instance_id})`
 *     endpoint. The wireless kpis endpoint is also in this category, but it similar
 *     to the others.
 *
 *     Open questions:
 *
 *     * Can we leave out subscription id in `/nfv/subinterfaces/{subscription_id}`? The query uses it, but I don't believe it is necessary.
 *     * Can we leave out `service='IP'` in `/nfv/subinterfaces/{subscription_id}/fw`? If so, we can merge it with the main nfv subinterfaces endpoint as they are otherwise the same query
 *
 *     The `traffic` prefix is open for discussion, but something is needed to
 *     distinguish these from other endpoints.
 */
@Injectable({ providedIn: "root" })
export class TrafficService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getServiceTrafficBps()` */
  static readonly GetServiceTrafficBpsPath = "/stats/services/{subscription_id}/bps";

  /**
   * Get Service Traffic Bps.
   *
   * Fetch traffic rates for a service subscription in bits per second.
   *
   * Args:
   *     subscription_id: Id of the service subscription.
   *     subscription_instance_ids: Optional. Ports, identified by subscription
   *         instance ids, to restrict the query to. Defaults to all of the
   *         service's ports.
   *     start: Range start, inclusive (unix seconds, multiple of `step`).
   *         Defaults to 8 hours ago (24 hours when `step` >= 1 hour).
   *     end: Range end, exclusive (unix seconds, multiple of `step`, not in
   *         the future). Defaults to now.
   *     step: Bucket size in seconds, minimum 60. Defaults to 60.
   *
   * Returns:
   *     One time series per statistic, each a list of `[timestamp, value]`
   *     pairs (value in bits per second):
   *
   *     - `in` / `out`: mean total rate per bucket.
   *     - `peakIn` / `peakOut`: busiest minute (max total rate) per bucket.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getServiceTrafficBps()` instead.
   *
   * This method doesn't expect any request body.
   */
  getServiceTrafficBps$Response(
    params: GetServiceTrafficBps$Params,
    context?: HttpContext,
  ): Promise<StrictHttpResponse<Traffic>> {
    const obs = getServiceTrafficBps(this.http, this.rootUrl, params, context);
    return firstValueFrom(obs);
  }

  /**
   * Get Service Traffic Bps.
   *
   * Fetch traffic rates for a service subscription in bits per second.
   *
   * Args:
   *     subscription_id: Id of the service subscription.
   *     subscription_instance_ids: Optional. Ports, identified by subscription
   *         instance ids, to restrict the query to. Defaults to all of the
   *         service's ports.
   *     start: Range start, inclusive (unix seconds, multiple of `step`).
   *         Defaults to 8 hours ago (24 hours when `step` >= 1 hour).
   *     end: Range end, exclusive (unix seconds, multiple of `step`, not in
   *         the future). Defaults to now.
   *     step: Bucket size in seconds, minimum 60. Defaults to 60.
   *
   * Returns:
   *     One time series per statistic, each a list of `[timestamp, value]`
   *     pairs (value in bits per second):
   *
   *     - `in` / `out`: mean total rate per bucket.
   *     - `peakIn` / `peakOut`: busiest minute (max total rate) per bucket.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getServiceTrafficBps$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getServiceTrafficBps(params: GetServiceTrafficBps$Params, context?: HttpContext): Promise<Traffic> {
    const resp = this.getServiceTrafficBps$Response(params, context);
    return resp.then((r: StrictHttpResponse<Traffic>): Traffic => r.body);
  }

  /** Path part for operation `getServiceTrafficPackets()` */
  static readonly GetServiceTrafficPacketsPath = "/stats/services/{subscription_id}/packets";

  /**
   * Get Service Traffic Packets.
   *
   * Fetch packet rates for a service subscription in packets per second.
   *
   * Args:
   *     subscription_id: Id of the service subscription.
   *     subscription_instance_ids: Optional. Ports, identified by subscription
   *         instance ids, to restrict the query to. Defaults to all of the
   *         service's ports.
   *     start: Range start, inclusive (unix seconds, multiple of `step`).
   *         Defaults to 8 hours ago (24 hours when `step` >= 1 hour).
   *     end: Range end, exclusive (unix seconds, multiple of `step`, not in
   *         the future). Defaults to now.
   *     step: Bucket size in seconds, minimum 60. Defaults to 60.
   *     packet_type: Kind of packets to count: unicast, broadcast or multicast.
   *
   * Returns:
   *     One time series per statistic, each a list of `[timestamp, value]`
   *     pairs (value in packets per second):
   *
   *     - `in` / `out`: mean total rate per bucket.
   *     - `peakIn` / `peakOut`: busiest minute (max total rate) per bucket.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getServiceTrafficPackets()` instead.
   *
   * This method doesn't expect any request body.
   */
  getServiceTrafficPackets$Response(
    params: GetServiceTrafficPackets$Params,
    context?: HttpContext,
  ): Promise<StrictHttpResponse<Traffic>> {
    const obs = getServiceTrafficPackets(this.http, this.rootUrl, params, context);
    return firstValueFrom(obs);
  }

  /**
   * Get Service Traffic Packets.
   *
   * Fetch packet rates for a service subscription in packets per second.
   *
   * Args:
   *     subscription_id: Id of the service subscription.
   *     subscription_instance_ids: Optional. Ports, identified by subscription
   *         instance ids, to restrict the query to. Defaults to all of the
   *         service's ports.
   *     start: Range start, inclusive (unix seconds, multiple of `step`).
   *         Defaults to 8 hours ago (24 hours when `step` >= 1 hour).
   *     end: Range end, exclusive (unix seconds, multiple of `step`, not in
   *         the future). Defaults to now.
   *     step: Bucket size in seconds, minimum 60. Defaults to 60.
   *     packet_type: Kind of packets to count: unicast, broadcast or multicast.
   *
   * Returns:
   *     One time series per statistic, each a list of `[timestamp, value]`
   *     pairs (value in packets per second):
   *
   *     - `in` / `out`: mean total rate per bucket.
   *     - `peakIn` / `peakOut`: busiest minute (max total rate) per bucket.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getServiceTrafficPackets$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getServiceTrafficPackets(params: GetServiceTrafficPackets$Params, context?: HttpContext): Promise<Traffic> {
    const resp = this.getServiceTrafficPackets$Response(params, context);
    return resp.then((r: StrictHttpResponse<Traffic>): Traffic => r.body);
  }

  /** Path part for operation `getServiceDdosTraffic()` */
  static readonly GetServiceDdosTrafficPath = "/stats/services/{subscription_id}/ddos-traffic";

  /**
   * Get Service Ddos Traffic.
   *
   * Fetch the rates at which a service's DDoS rate-limit filters are dropping traffic.
   *
   * Args:
   *     subscription_id: Id of the service subscription.
   *     ip_version: IP version to query: 4 or 6.
   *     start: Range start, inclusive (unix seconds, multiple of `step`).
   *         Defaults to 8 hours ago (24 hours when `step` >= 1 hour).
   *     end: Range end, exclusive (unix seconds, multiple of `step`, not in
   *         the future). Defaults to now.
   *     step: Bucket size in seconds, minimum 60. Defaults to 60.
   *     unit: Unit of the returned rates: bits or packets. Defaults to bits.
   *
   * Returns:
   *     One time series per firewall filter counter, each a `name` (the
   *     filter, e.g. `ntp`) and a list of `[timestamp, value]` pairs (value
   *     in `unit` per second). Counters that were zero over the whole range
   *     are omitted.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getServiceDdosTraffic()` instead.
   *
   * This method doesn't expect any request body.
   */
  getServiceDdosTraffic$Response(
    params: GetServiceDdosTraffic$Params,
    context?: HttpContext,
  ): Promise<StrictHttpResponse<Array<DdosRate>>> {
    const obs = getServiceDdosTraffic(this.http, this.rootUrl, params, context);
    return firstValueFrom(obs);
  }

  /**
   * Get Service Ddos Traffic.
   *
   * Fetch the rates at which a service's DDoS rate-limit filters are dropping traffic.
   *
   * Args:
   *     subscription_id: Id of the service subscription.
   *     ip_version: IP version to query: 4 or 6.
   *     start: Range start, inclusive (unix seconds, multiple of `step`).
   *         Defaults to 8 hours ago (24 hours when `step` >= 1 hour).
   *     end: Range end, exclusive (unix seconds, multiple of `step`, not in
   *         the future). Defaults to now.
   *     step: Bucket size in seconds, minimum 60. Defaults to 60.
   *     unit: Unit of the returned rates: bits or packets. Defaults to bits.
   *
   * Returns:
   *     One time series per firewall filter counter, each a `name` (the
   *     filter, e.g. `ntp`) and a list of `[timestamp, value]` pairs (value
   *     in `unit` per second). Counters that were zero over the whole range
   *     are omitted.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getServiceDdosTraffic$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getServiceDdosTraffic(params: GetServiceDdosTraffic$Params, context?: HttpContext): Promise<Array<DdosRate>> {
    const resp = this.getServiceDdosTraffic$Response(params, context);
    return resp.then((r: StrictHttpResponse<Array<DdosRate>>): Array<DdosRate> => r.body);
  }
}
